import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import ConversationList from "../components/unibox/ConversationList";
import ChatPane from "../components/unibox/ChatPane";
import ContactPanel from "../components/unibox/ContactPanel";
import FiltersModal, {
  EMPTY_FILTERS,
  countActiveFilters,
  filtersToQueryParams,
  applyClientSideFilters,
} from "../components/unibox/FiltersModal";
import {
  getMessageThreads,
  getMessageThread,
  markThreadRead,
  replyToMessage,
  favoriteThread,
  unfavoriteThread,
  syncInboxMessages,
  getLinkedInAccounts,
  getCampaignsPromise,
} from "../services/apiService";
import { useTranslation } from "react-i18next";
import { notifyError } from "../helpers/apiError";

const PAGE_SIZE = 30;

export default function Messages() {
  usePageTitle("messages");
  
  const { t } = useTranslation();

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTERS });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [senders, setSenders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);

  const [selected, setSelected] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getLinkedInAccounts({ limit: 100 })
      .then((res) => setSenders(res.data?.data || res.data || []))
      .catch(() => setSenders([]));
    getCampaignsPromise({ limit: 100 })
      .then((res) => setCampaigns(res.data?.data || res.data || []))
      .catch(() => setCampaigns([]));
  }, []);

  const fetchThreads = useCallback(
    async ({ pageNum = 1, append = false } = {}) => {
      setListLoading(true);
      try {
        const params = {
          page: pageNum,
          limit: PAGE_SIZE,
          includeStats: true,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(tab === "unread" ? { unreadOnly: true } : {}),
          ...(tab === "favorites" ? { favoritesOnly: true } : {}),
          ...filtersToQueryParams(filters),
        };
        const res = await getMessageThreads(params);
        const payload = res.data || {};
        const rows = applyClientSideFilters(payload.data || [], filters);
        setThreads((prev) => (append ? [...prev, ...rows] : rows));
        setPage(pageNum);
        // Client filters can shrink the page — hide "load more" if we filtered heavily
        const filteredHard =
          filters.lastMessageFrom !== "ALL"
          || filters.archived === "ARCHIVED"
          || (filters.senderIds?.length || 0) > 1
          || (filters.campaignIds?.length || 0) > 1;
        setHasMore(Boolean(payload.hasNext) && !filteredHard);
        if (tab === "all" && !append) {
          if (payload.stats?.unreadThreads != null) {
            setUnreadTotal(payload.stats.unreadThreads);
          } else {
            setUnreadTotal(
              rows.reduce((sum, row) => sum + (row.unreadCount > 0 ? 1 : 0), 0),
            );
          }
        }
      } catch (err) {
        notifyError(err);
        if (!append) setThreads([]);
      } finally {
        setListLoading(false);
      }
    },
    [debouncedSearch, tab, filters],
  );

  useEffect(() => {
    fetchThreads({ pageNum: 1, append: false });
  }, [fetchThreads]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    for (const thread of threads) {
      // tags come from leadContext on detail; list may not have them
      (thread.lead?.tags || []).forEach((t) => set.add(t));
    }
    ["interested", "not-interested", "follow-up", "hot", "warm", "cold"].forEach(
      (t) => set.add(t),
    );
    return [...set].sort().map((t) => ({ id: t, label: t }));
  }, [threads]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const loadThread = useCallback(async (thread) => {
    if (!thread?.threadId) return;
    setSelected(thread);
    setThreadLoading(true);
    setReplyContent("");
    try {
      const res = await getMessageThread(thread.threadId);
      const detail = res.data;
      setThreadDetail(detail);
      if ((thread.unreadCount || 0) > 0) {
        await markThreadRead(thread.threadId).catch(() => null);
        setThreads((prev) =>
          prev.map((row) =>
            row.threadId === thread.threadId ? { ...row, unreadCount: 0 } : row,
          ),
        );
        setUnreadTotal((n) => Math.max(0, n - 1));
        setSelected((prev) =>
          prev?.threadId === thread.threadId ? { ...prev, unreadCount: 0 } : prev,
        );
      }
    } catch (err) {
      notifyError(err);
      setThreadDetail(null);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const handleSend = async () => {
    const content = replyContent.trim();
    if (!content || !threadDetail?.messages?.length) return;
    const parentId = threadDetail.messages[threadDetail.messages.length - 1]?.id;
    if (!parentId) {
      toast.error("No message to reply to");
      return;
    }
    setSending(true);
    try {
      await replyToMessage(parentId, { content });
      setReplyContent("");
      toast.success("Message sent");
      await loadThread(selected);
      fetchThreads({ pageNum: 1, append: false });
    } catch (err) {
      notifyError(err);
    } finally {
      setSending(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!selected?.threadId) return;
    const next = !(threadDetail?.isFavorite || selected.isFavorite);
    try {
      if (next) {
        await favoriteThread(selected.threadId);
      } else {
        await unfavoriteThread(selected.threadId);
      }
      setThreadDetail((prev) => (prev ? { ...prev, isFavorite: next } : prev));
      setSelected((prev) => (prev ? { ...prev, isFavorite: next } : prev));
      setThreads((prev) =>
        prev.map((row) =>
          row.threadId === selected.threadId ? { ...row, isFavorite: next } : row,
        ),
      );
      if (tab === "favorites" && !next) {
        setThreads((prev) => prev.filter((row) => row.threadId !== selected.threadId));
      }
    } catch (err) {
      notifyError(err);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncInboxMessages({});
      toast.success("Inbox sync started");
      setTimeout(() => fetchThreads({ pageNum: 1, append: false }), 1500);
    } catch (err) {
      notifyError(err);
    } finally {
      setSyncing(false);
    }
  };

  const isFavorite = useMemo(
    () => Boolean(threadDetail?.isFavorite || selected?.isFavorite),
    [threadDetail?.isFavorite, selected?.isFavorite],
  );

  return (
    <div className="py-[20px]">
      <h1 className="text-[26px] font-bold text-gray-900 mb-4">{t("messages.title")}</h1>
    <div className="h-[calc(100vh-150px)] min-h-[560px] bg-white overflow-hidden flex flex-col rounded-xl border border-gray-200 shadow-sm">
      <div className="px-3 py-2 border-b border-slate-100 flex justify-end bg-white">
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {syncing ? "Syncing…" : "Sync inbox"}
        </button>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] lg:grid-cols-[320px_minmax(0,1fr)_300px] min-h-0">
        <ConversationList
          threads={threads}
          selectedThreadId={selected?.threadId}
          tab={tab}
          unreadTotal={unreadTotal}
          search={search}
          loading={listLoading}
          activeFilterCount={activeFilterCount}
          onTabChange={(next) => {
            setTab(next);
            setSelected(null);
            setThreadDetail(null);
          }}
          onSearchChange={setSearch}
          onOpenFilters={() => {
            setFilterDraft({ ...filters });
            setFiltersOpen(true);
          }}
          onSelect={loadThread}
          hasMore={hasMore}
          onLoadMore={() => fetchThreads({ pageNum: page + 1, append: true })}
        />
        <ChatPane
          threadSummary={selected}
          threadDetail={threadDetail}
          loading={threadLoading}
          replyContent={replyContent}
          sending={sending}
          isFavorite={isFavorite}
          onReplyChange={setReplyContent}
          onSend={handleSend}
          onToggleFavorite={handleToggleFavorite}
          onRefresh={() => selected && loadThread(selected)}
        />
        <ContactPanel threadSummary={selected} leadContext={threadDetail?.leadContext} />
      </div>

      <FiltersModal
        open={filtersOpen}
        draft={filterDraft}
        onChange={setFilterDraft}
        onClose={() => setFiltersOpen(false)}
        onClear={() => setFilterDraft({ ...EMPTY_FILTERS })}
        onApply={() => {
          setFilters({ ...filterDraft });
          setFiltersOpen(false);
          setSelected(null);
          setThreadDetail(null);
        }}
        senders={senders}
        campaigns={campaigns}
        tagOptions={tagOptions}
      />
    </div>
    </div>
  );
}
